// Owner-only "Share" popover, reused for both docs and folders. Lists the owner
// and current viewers/editors, lets the owner add (by @cellery.ai email + role)
// or remove people, and reports the new ACL up via onChange. The actual write is
// delegated to `onApply` (doc → /access, folder → /folder-access); clients never
// write docs/* or folders/* directly.

import { useEffect, useRef, useState } from "react";
import type { Role } from "../types";
import type { AccessChange } from "../access";
import { Icon } from "./Icon";

export interface ShareResult {
  viewers: string[];
  editors: string[];
  members?: string[];
}

interface Props {
  title?: string;
  ownerEmail: string;
  viewers: string[];
  editors: string[];
  onApply: (change: AccessChange) => Promise<ShareResult>;
  onChange: (next: ShareResult) => void;
  onClose: () => void;
  note?: string;
}

interface Row {
  email: string;
  label: string;
  removable: boolean;
}

export function SharePanel({
  title = "Share",
  ownerEmail,
  viewers,
  editors,
  onApply,
  onChange,
  onClose,
  note,
}: Props) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const apply = async (change: AccessChange) => {
    setBusy(true);
    setError(null);
    try {
      const next = await onApply(change);
      onChange(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    await apply({ add: [{ email: value, role }] });
    setEmail("");
  };

  const rows: Row[] = [
    { email: ownerEmail, label: "Owner", removable: false },
    ...editors.map((e) => ({ email: e, label: "Editor", removable: true })),
    ...viewers.map((e) => ({ email: e, label: "Viewer", removable: true })),
  ];

  return (
    <div className="share-popover" role="dialog" aria-label={title} ref={panelRef}>
      <div className="share-head">
        <strong>{title}</strong>
        <button
          type="button"
          className="btn btn-ghost tap icon-btn"
          onClick={onClose}
          aria-label="Close sharing"
        >
          <Icon name="close" size={18} />
        </button>
      </div>

      {note && <p className="caption share-note">{note}</p>}

      <ul className="share-list">
        {rows.map((r) => (
          <li key={`${r.label}:${r.email}`} className="share-row">
            <span className="share-email" title={r.email}>
              {r.email}
            </span>
            <span className="chip">{r.label}</span>
            <button
              type="button"
              className="btn btn-ghost tap icon-btn"
              onClick={() => apply({ remove: [r.email] })}
              disabled={busy || !r.removable}
              aria-label={r.removable ? `Remove ${r.email}` : "Owner cannot be removed"}
              style={r.removable ? undefined : { visibility: "hidden" }}
            >
              <Icon name="close" size={16} />
            </button>
          </li>
        ))}
      </ul>

      <form className="share-add" onSubmit={onAdd}>
        <input
          className="input share-input"
          type="email"
          placeholder="name@cellery.ai"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          aria-label="Email to share with"
        />
        <select
          className="input"
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          disabled={busy}
          aria-label="Role"
        >
          <option value="viewer">Viewer</option>
          <option value="editor">Editor</option>
        </select>
        <button
          type="submit"
          className="btn btn-primary share-add-btn"
          disabled={busy || !email.trim()}
        >
          Add
        </button>
      </form>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
